import { useEffect } from 'react';
import { hapticFeedback, viewport } from '@telegram-apps/sdk-react';
import { Dialog, DialogTitle } from '@headlessui/react';
import clsx from 'clsx';
import { motion } from 'motion/react';
import PropTypes from 'prop-types';
import Button from '@components/ui/Button.component.jsx';
import Hr from '@components/ui/Hr.component.jsx';

const MotionPanel = motion.create('div');

function FormDialog({
  open,
  onClose,
  onButtonClick,
  onSecondaryButtonClick,
  title,
  buttonText,
  secondaryButtonText,
  children,
  variant = 'dark',
  height,
}) {
  useEffect(() => {
    hapticFeedback.selectionChanged();
  }, [open]);

  if (!open) return null;

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 flex w-screen items-center justify-center h-screen z-20">
        <MotionPanel
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, ease: 'easeInOut' }}
          exit={{ opacity: 0 }}
          style={{
            paddingTop: viewport.contentSafeAreaInsetTop(),
          }}
          className={clsx(
            'max-w-lg w-full h-full space-y-4 rounded-xl p-4 shadow-xl border',
            {
              'bg-event-light border-corporate-gray/20': variant === 'light',
              'bg-event-deep border-corporate-dark/20': variant !== 'light',
            },
          )}
        >
          <DialogTitle
            className={clsx('font-wide text-xl font-semibold text-center', {
              'text-event-deep-dark/80': variant === 'light',
              'text-event-light': variant !== 'light',
            })}
          >
            {title}
          </DialogTitle>
          <Hr variant={variant === 'light' ? 'dark' : 'light'} />
          <div className={clsx('overflow-y-auto h-vh', height)}>{children}</div>

          {(buttonText || secondaryButtonText) && (
            <div className="flex justify-center gap-1">
              {secondaryButtonText && (
                <Button
                  className={'max-h-12'}
                  onButtonClick={onSecondaryButtonClick ?? onClose}
                  buttonText={secondaryButtonText}
                  variant="secondary"
                />
              )}
              {buttonText && (
                <Button
                  className={'max-h-12'}
                  onButtonClick={onButtonClick ?? onClose}
                  buttonText={buttonText}
                  variant="primary"
                />
              )}
            </div>
          )}
        </MotionPanel>
      </div>
    </Dialog>
  );
}

FormDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  buttonText: PropTypes.string,
  children: PropTypes.node,
  variant: PropTypes.oneOf(['dark', 'light']),
};

export default FormDialog;
